/**
 * HEIGHT RULES — the metric height limits AlderCAD checks a building against (2026-10-07,
 * Matt). Edited where the research lives: on a zone or special overlay (Permitting, Zones &
 * Jurisdictions) and on an HOA (Parcel Research, under the HOA). Shared: a rule serves every
 * parcel on that zone or HOA, and there is no per-project override anywhere — fixing a rule
 * here fixes it for every project. An HOA may have several rules split by lot (Dover Shores:
 * two-story lots 25', lot 81 16', every other lot 14'); the parcel's tract and lot are read
 * from its Legal Description. See docs/DEVELOPMENT_STANDARDS.md.
 *
 * Props: kind 'zone' | 'organization', sourceId (Airtable record id), sourceName, byName.
 */
import { useEffect, useState } from 'react'
import { listHeightRules, saveHeightRule, deleteHeightRule } from '../services/developmentStandards.js'
import { alertDialog, confirmDialog } from './Dialog.jsx'

const ZONE_FIELDS = [
  ['flat_limit_ft', 'Flat roof limit (ft)', 'Including guardrails and parapet walls'],
  ['sloped_limit_ft', 'Sloped roof limit (ft)', 'To the highest ridge; a roof counts as sloped at 3:12 or steeper'],
  ['chimney_allowance_ft', 'Chimney allowance (ft)', 'How far a chimney may rise over the limit'],
  ['flood_slab_min_elev_ft', 'Flood zone top of slab (elev.)', 'Minimum top of slab height is measured from in a flood hazard area'],
]
const HOA_FIELDS = [
  ['limit_ft', 'Height limit (ft)', 'Above the same established grade the city uses, never a floor or slab'],
  ['chimney_allowance_ft', 'Chimney allowance (ft)', 'Blank when the CC&Rs say nothing about chimneys (not limited); 0 when they hold chimneys to the limit'],
]

const blank = (kind) => ({
  label: '', tract: '', lots: '', citation: '', notes: '',
  params: kind === 'zone' ? { flat_limit_ft: '', sloped_limit_ft: '', chimney_allowance_ft: '', flood_slab_min_elev_ft: '' } : { limit_ft: '', chimney_allowance_ft: '' },
})

export default function HeightRulesPanel({ kind, sourceId, sourceName, byName }) {
  const [rules, setRules] = useState(null)
  const [editing, setEditing] = useState(null)   // a rule (with id) or a new draft
  const [busy, setBusy] = useState(false)
  const isZone = kind === 'zone'
  const fields = isZone ? ZONE_FIELDS : HOA_FIELDS

  useEffect(() => {
    setRules(null); setEditing(null)
    if (!sourceId) return
    listHeightRules(kind, sourceId).then(setRules).catch(() => setRules([]))
  }, [kind, sourceId])

  async function save() {
    setBusy(true)
    try {
      const row = await saveHeightRule({ ...editing, source_kind: kind, source_airtable_id: sourceId, source_name: sourceName }, byName)
      setRules((rs) => (editing.id ? rs.map((r) => (r.id === row.id ? row : r)) : [...(rs || []), row]))
      setEditing(null)
    } catch (err) {
      await alertDialog({ title: 'Not saved', message: err.message || 'The rule could not be saved.' })
    }
    setBusy(false)
  }
  async function remove(r) {
    if (!(await confirmDialog({ title: 'Delete this height rule?', message: `Every project on ${sourceName || 'this record'} stops being checked against it.`, confirmLabel: 'Delete rule', danger: true }))) return
    try { await deleteHeightRule(r.id); setRules((rs) => rs.filter((x) => x.id !== r.id)) }
    catch (err) { await alertDialog({ title: 'Not deleted', message: err.message || 'The rule could not be deleted.' }) }
  }

  const summary = (r) => {
    const p = r.params || {}
    const lim = isZone
      ? `${p.flat_limit_ft ?? '?'}' flat · ${p.sloped_limit_ft ?? '?'}' sloped${p.chimney_allowance_ft ? ` · chimneys ${p.chimney_allowance_ft}' over` : ''}`
      : `${p.limit_ft ?? '?'}'${p.chimney_allowance_ft === undefined || p.chimney_allowance_ft === null || p.chimney_allowance_ft === '' ? ' · chimneys not limited' : Number(p.chimney_allowance_ft) > 0 ? ` · chimneys ${p.chimney_allowance_ft}' over` : ' · chimneys held to the limit'}`
    const where = [r.tract ? `Tract ${r.tract}` : '', r.lots ? `${/[,;-]/.test(r.lots) ? 'Lots' : 'Lot'} ${r.lots}` : (r.tract || rules.some((x) => x.lots) ? 'every other lot' : '')].filter(Boolean).join(', ')
    return { lim, where }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={S.title}>Height rules — checked in AlderCAD</div>
      <div style={S.hint}>
        {isZone
          ? 'The limits every project on this zone is checked against. No project can change them; fix them here.'
          : 'The limits every parcel in this HOA is checked against. Split by lot when the CC&Rs do; the lot comes from the parcel’s legal description.'}
      </div>
      {rules == null ? <div style={S.hint}>Loading…</div> : (
        <>
          {rules.length === 0 && !editing && <div style={S.empty}>No height rule on file. AlderCAD will say so on the drawings until one is added.</div>}
          {rules.map((r) => {
            if (editing && editing.id === r.id) return null
            const { lim, where } = summary(r)
            return (
              <div key={r.id} style={S.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.rowMain}>{r.label ? <b>{r.label}: </b> : null}<span style={S.mono}>{lim}</span></div>
                  <div style={S.rowSub}>{[where, r.citation].filter(Boolean).join(' · ') || 'No citation'}</div>
                  {r.notes && <div style={S.rowSub}>{r.notes}</div>}
                </div>
                <button style={S.link} onClick={() => setEditing({ ...r, params: { ...r.params } })}>Edit</button>
                <button style={{ ...S.link, color: 'var(--aj-danger, #c0392b)' }} onClick={() => remove(r)}>Delete</button>
              </div>
            )
          })}
          {editing && (
            <div style={S.form}>
              {!isZone && (
                <>
                  <Field label="Applies to" hint="A name for this rule, e.g. Two-story lots">
                    <input style={S.input} value={editing.label || ''} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                  </Field>
                  <Field label="Tract" hint="Leave blank if the rule covers every tract">
                    <input style={S.input} value={editing.tract || ''} onChange={(e) => setEditing({ ...editing, tract: e.target.value })} placeholder="4224" />
                  </Field>
                  <Field label="Lots" hint="Leave blank for every lot not listed in another rule">
                    <input style={S.input} value={editing.lots || ''} onChange={(e) => setEditing({ ...editing, lots: e.target.value })} placeholder="1-7, 101-180, 215-311" />
                  </Field>
                </>
              )}
              {fields.map(([key, label, hint]) => (
                <Field key={key} label={label} hint={hint}>
                  <input style={{ ...S.input, width: 110 }} type="number" step="0.01"
                    value={editing.params?.[key] ?? ''}
                    onChange={(e) => setEditing({ ...editing, params: { ...editing.params, [key]: e.target.value } })} />
                </Field>
              ))}
              <Field label="Citation" hint="Where the rule is written">
                <input style={S.input} value={editing.citation || ''} onChange={(e) => setEditing({ ...editing, citation: e.target.value })} placeholder={isZone ? 'NBMC 20.18.030 Table 2-2' : 'CC&Rs Art. VI Sec. 7'} />
              </Field>
              <Field label="Notes" hint="Anything the numbers do not capture">
                <textarea style={{ ...S.input, minHeight: 48, fontFamily: '"DM Sans", sans-serif' }} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </Field>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button style={S.primary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save rule'}</button>
                <button style={S.ghost} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          )}
          {!editing && (isZone ? rules.length === 0 : true) && (
            <button style={S.add} onClick={() => setEditing(blank(kind))}>+ Add height rule</button>
          )}
        </>
      )}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
      <div style={{ width: 180, flexShrink: 0, paddingTop: 4 }}>
        <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: 'var(--aj-text, #222)' }}>{label}</div>
        {hint && <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--aj-text-muted, #888)', lineHeight: 1.3 }}>{hint}</div>}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </label>
  )
}

const S = {
  title: { fontFamily: 'Montserrat, sans-serif', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid, #555)', marginBottom: 4 },
  hint: { fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--aj-text-muted, #888)', marginBottom: 8 },
  empty: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: 'var(--aj-danger, #c0392b)', marginBottom: 8 },
  row: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0', borderBottom: '0.5px solid var(--aj-border, #eee)' },
  rowMain: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text, #222)' },
  rowSub: { fontFamily: 'DM Sans, sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted, #888)', marginTop: 2 },
  mono: { fontFamily: '"DM Mono", monospace', fontSize: 12.5 },
  link: { background: 'none', border: 'none', padding: '2px 4px', fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--aj-accent-blue, #2563eb)', cursor: 'pointer' },
  form: { padding: '10px 12px', border: '0.5px solid var(--aj-border-mid, #ccc)', borderRadius: 10, background: 'var(--aj-bg-subtle, #fafafa)', marginTop: 8 },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Mono", monospace', fontSize: 12, padding: '4px 6px', border: '0.5px solid var(--aj-border-mid, #ccc)', borderRadius: 4, background: 'var(--aj-bg-surface, #fff)', color: 'var(--aj-text, #222)' },
  primary: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '5px 12px', borderRadius: 4, border: 'none', background: 'var(--aj-accent, #07272D)', color: '#fff', cursor: 'pointer' },
  ghost: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '5px 12px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid, #ccc)', background: 'none', color: 'var(--aj-text, #222)', cursor: 'pointer' },
  add: { marginTop: 8, background: 'none', border: 'none', padding: 0, fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: 'var(--aj-accent-blue, #2563eb)', cursor: 'pointer' },
}
